import React from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import CalendarGridLarge from './CalendarGridLarge';
import ScheduledSection from './ScheduledSection';
import TeamsRotationSection from './TeamsRotationSection';
import { PlanningFilters } from './PlanningFilters';
import { PlanningWeekView } from '@/components/planning/PlanningWeekView';
import { PlanningExportButton } from '@/components/planning/PlanningExportButton';
import type { CalendarDay, CalendarAffectation, DayPlanning } from '@/types/calendar';
import type { MembreDisponibilite } from '@/hooks/useDisponibilites';
import type { ViewMode } from '@/components/calendar/hooks/useCalendar';

interface PlanningPanelExpandedProps {
  isVisible: boolean;
  onCollapse: () => void;
  // Données calendrier
  calendarDays: CalendarDay[];
  weekDays: string[];
  currentMonthLabel: string;
  currentYear: string;
  selectedDate: Date;
  currentDate: Date;
  selectedDayPlanning: DayPlanning | null;
  loading: boolean;
  // Vue mode
  viewMode: ViewMode;
  viewLabel: string;
  onToggleViewMode: () => void;
  affectations: CalendarAffectation[];
  // Navigation
  onGoToToday: () => void;
  onGoToPrevMonth: () => void;
  onGoToNextMonth: () => void;
  onDayClick: (date: Date) => void;
  // Actions affectations
  onEditAffectation: (affectation: CalendarAffectation) => void;
  onDeleteAffectation: (id: string) => void;
  onCreateAffectation: () => void;
  // Props pour section rotation
  activeTab: 'planning' | 'equipes';
  onTabChange: (tab: 'planning' | 'equipes') => void;
  membresDisponibles: MembreDisponibilite[];
  membresAffectes: MembreDisponibilite[];
  chantiers: any[];
  equipes: any[];
  membres: any[];
  entrepriseId: string;
  entrepriseNom?: string;
  onRotationComplete?: () => void;
}

/**
 * Panneau Planning déplié (65vw, min 900px, max 1400px)
 * Contient : Header avec tabs, Calendrier spacieux, Section Scheduled ou Rotation
 */
const PlanningPanelExpanded = ({
  isVisible,
  onCollapse,
  calendarDays,
  weekDays,
  currentMonthLabel,
  currentYear,
  selectedDate,
  currentDate,
  selectedDayPlanning,
  loading,
  // Vue mode
  viewMode,
  viewLabel,
  onToggleViewMode,
  affectations,
  // Navigation
  onGoToToday,
  onGoToPrevMonth,
  onGoToNextMonth,
  onDayClick,
  onEditAffectation,
  onDeleteAffectation,
  onCreateAffectation,
  // Props rotation
  activeTab,
  onTabChange,
  membresDisponibles,
  membresAffectes,
  chantiers,
  equipes,
  membres,
  entrepriseId,
  entrepriseNom,
  onRotationComplete,
}: PlanningPanelExpandedProps) => {
  if (!isVisible) return null;

  return (
    <div
      className={cn(
        // Position fixe à droite, pleine hauteur (z-[60] pour passer au-dessus du bouton Guide)
        'fixed right-0 top-0 bottom-0 z-[60]',
        // Dimensions : 65vw avec min/max
        'w-[65vw] min-w-[700px] max-w-[1400px]',
        // Style visuel
        'bg-background/95 backdrop-blur-md',
        'border-l border-border/50',
        'shadow-2xl shadow-black/40',
        // Flex column pour le layout
        'flex flex-col'
      )}
    >
      {/* ═══════════════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════════════ */}
      <div className="px-6 py-4 border-b border-border/30 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Planning des équipes</h2>
              <p className="text-sm text-muted-foreground">
                Visualisez et gérez les affectations
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Bouton Export PDF */}
            <PlanningExportButton
              viewMode={viewMode}
              currentDate={currentDate}
              affectations={affectations}
              entrepriseNom={entrepriseNom}
              chantiers={chantiers}
              equipes={equipes}
            />

            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 hover:bg-destructive/10 hover:text-destructive"
              onClick={onCollapse}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs : Planning | Équipes */}
        <div className="flex border border-border rounded-lg overflow-hidden mt-4">
          <button
            onClick={() => onTabChange('planning')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === 'planning'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground'
            )}
          >
            <Calendar className="w-4 h-4" />
            Planning
          </button>
          <button
            onClick={() => onTabChange('equipes')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-l border-border',
              activeTab === 'equipes'
                ? 'bg-orange-500 text-white'
                : 'hover:bg-muted text-muted-foreground'
            )}
          >
            <Users className="w-4 h-4" />
            Équipes & Rotation
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          CONTENU PRINCIPAL - Conditionnel selon l'onglet actif
          ═══════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === 'planning' ? (
          <>
            {/* Section calendrier */}
            <div className="p-6 flex-shrink-0">
              {/* Navigation + Toggle Semaine/Mois */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">
                  <span className="text-primary capitalize">{viewLabel}</span>
                </h3>

                <div className="flex items-center gap-3">
                  {/* Toggle Semaine / Mois */}
                  <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5">
                    <span className={cn(
                      "text-sm font-semibold transition-colors",
                      viewMode === 'semaine' ? 'text-amber-500' : 'text-muted-foreground'
                    )}>
                      Sem.
                    </span>
                    <button
                      onClick={onToggleViewMode}
                      className="relative w-10 h-5 rounded-full bg-muted border border-border transition-colors"
                    >
                      <div
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-amber-500 transition-transform",
                          viewMode === 'semaine' ? 'left-0.5' : 'left-5'
                        )}
                      />
                    </button>
                    <span className={cn(
                      "text-sm font-semibold transition-colors",
                      viewMode === 'mois' ? 'text-amber-500' : 'text-muted-foreground'
                    )}>
                      Mois
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onGoToToday}
                    className="font-medium"
                  >
                    Aujourd'hui
                  </Button>
                  <div className="flex border border-border rounded-lg overflow-hidden">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none hover:bg-muted"
                      onClick={onGoToPrevMonth}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-none border-l hover:bg-muted"
                      onClick={onGoToNextMonth}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Barre de filtres */}
              <div className="mb-4">
                <PlanningFilters
                  chantiers={chantiers}
                  equipes={equipes}
                  membres={membres}
                />
              </div>

              {/* Grille du calendrier - Vue Semaine ou Mois */}
              {loading ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>Chargement...</span>
                  </div>
                </div>
              ) : viewMode === 'semaine' ? (
                <PlanningWeekView
                  currentDate={currentDate}
                  affectations={affectations}
                  selectedDate={selectedDate}
                  onDayClick={onDayClick}
                />
              ) : (
                <CalendarGridLarge
                  days={calendarDays}
                  weekDays={weekDays}
                  onDayClick={onDayClick}
                />
              )}
            </div>

            {/* Section Scheduled (scrollable) */}
            <div className="flex-1 min-h-0 border-t border-border/30">
              <ScheduledSection
                selectedDate={selectedDate}
                planning={selectedDayPlanning}
                onEdit={onEditAffectation}
                onDelete={onDeleteAffectation}
                onCreate={onCreateAffectation}
              />
            </div>
          </>
        ) : (
          /* Section Équipes & Rotation */
          <TeamsRotationSection
            membresDisponibles={membresDisponibles}
            membresAffectes={membresAffectes}
            chantiers={chantiers}
            entrepriseId={entrepriseId}
            onRotationComplete={onRotationComplete}
          />
        )}
      </div>
    </div>
  );
};

export default PlanningPanelExpanded;
